import {
	Box,
	CopyButton,
	List,
	Modal,
	Text,
	UnstyledButton
} from '@mantine/core';
import { AudioLines, Check, Copy, Smartphone, X } from 'lucide-react';
import type { FC } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import { CLASS_NAMES } from '../../classNames';
import {
	STEPPER_SETUP_COMPLETE_CALL_STARTED_IMAGE_URL,
	STEPPER_SETUP_COMPLETE_CHECKLIST_KEYS,
	STEPPER_SETUP_COMPLETE_MODAL_RADIUS,
	STEPPER_SETUP_COMPLETE_MODAL_SIZE,
	STEPPER_SETUP_COMPLETE_TEST_CALL_VIDEO_URL
} from '../../constants';

interface StepperSetupCompleteTestCallCardProps {
	displayedAgentPhoneNumber: string;
	isWaitingStatusVisible: boolean;
	onClose: () => void;
}

const StepperSetupCompleteTestCallCard: FC<
	StepperSetupCompleteTestCallCardProps
> = ({ displayedAgentPhoneNumber, isWaitingStatusVisible, onClose }) => {
	const { localization } = useHostContext();
	const canCopyPhoneNumber = displayedAgentPhoneNumber !== '---';

	return (
		<Modal
			aria-labelledby="stepper-setup-complete-title"
			centered
			classNames={{
				body: CLASS_NAMES.setupCompleteModalBody,
				content: CLASS_NAMES.setupCompleteModalContent
			}}
			closeOnClickOutside={isWaitingStatusVisible}
			closeOnEscape={isWaitingStatusVisible}
			onClose={onClose}
			opened
			radius={STEPPER_SETUP_COMPLETE_MODAL_RADIUS}
			size={STEPPER_SETUP_COMPLETE_MODAL_SIZE}
			withCloseButton={false}
		>
			<Box
				className={CLASS_NAMES.setupCompleteCard}
				data-testid="stepper-setup-complete-test-call-modal"
			>
				<Box className={CLASS_NAMES.setupCompleteHeader}>
					<Box className={CLASS_NAMES.setupCompleteHeaderTop}>
						<Smartphone
							aria-hidden="true"
							className={CLASS_NAMES.setupCompletePhoneIcon}
						/>
						<UnstyledButton
							aria-label={localization.translate('common', 'close')}
							className={CLASS_NAMES.setupCompleteCloseButton}
							data-testid="stepper-setup-complete-test-call-close-button"
							onClick={onClose}
							type="button"
						>
							<X
								aria-hidden="true"
								className={CLASS_NAMES.setupCompleteCloseIcon}
							/>
						</UnstyledButton>
					</Box>

					<Text
						className={CLASS_NAMES.setupCompleteCallTitle}
						component="h2"
						id="stepper-setup-complete-title"
					>
						{isWaitingStatusVisible ? (
							<>
								{localization.translate(
									'creator',
									'stepperOnboarding.setupComplete.callTitlePrefix'
								)}{' '}
								<Box
									className={CLASS_NAMES.setupCompletePhoneNumberInline}
									component="span"
								>
									<Box
										component="span"
										data-testid="stepper-setup-complete-phone-number"
									>
										{displayedAgentPhoneNumber}
									</Box>
									{canCopyPhoneNumber ? (
										<CopyButton value={displayedAgentPhoneNumber}>
											{({ copied, copy }) => (
												<UnstyledButton
													aria-label={localization.translate(
														'common',
														'copy_to_clipboard'
													)}
													className={CLASS_NAMES.setupCompleteCopyButton}
													data-testid="stepper-setup-complete-phone-copy-button"
													onClick={copy}
													type="button"
												>
													{copied ? (
														<Check
															aria-hidden="true"
															className={CLASS_NAMES.setupCompleteCopyIcon}
														/>
													) : (
														<Copy
															aria-hidden="true"
															className={CLASS_NAMES.setupCompleteCopyIcon}
														/>
													)}
												</UnstyledButton>
											)}
										</CopyButton>
									) : null}
								</Box>
							</>
						) : (
							localization.translate(
								'creator',
								'stepperOnboarding.setupComplete.activeCallTitle'
							)
						)}
					</Text>
				</Box>

				{isWaitingStatusVisible ? (
					<video
						autoPlay
						className={CLASS_NAMES.setupCompleteVideo}
						data-testid="stepper-setup-complete-test-call-video"
						loop
						muted
						playsInline
						src={STEPPER_SETUP_COMPLETE_TEST_CALL_VIDEO_URL}
					/>
				) : (
					<img
						alt=""
						aria-hidden="true"
						className={CLASS_NAMES.setupCompleteCallStartedImage}
						data-testid="stepper-setup-complete-call-started-image"
						src={STEPPER_SETUP_COMPLETE_CALL_STARTED_IMAGE_URL}
					/>
				)}

				<Box className={CLASS_NAMES.setupCompleteContent}>
					<Text
						className={CLASS_NAMES.setupCompleteCallTitle}
						component="h3"
					>
						{localization.translate(
							'creator',
							'stepperOnboarding.setupComplete.customerPromptTitle'
						)}
					</Text>
					<List className={CLASS_NAMES.setupCompleteChecklist}>
						{STEPPER_SETUP_COMPLETE_CHECKLIST_KEYS.map((key) => (
							<List.Item key={key}>
								{localization.translate(
									'creator',
									`stepperOnboarding.setupComplete.checklist.${key}`
								)}
							</List.Item>
						))}
					</List>

					{isWaitingStatusVisible ? (
						<Box className={CLASS_NAMES.setupCompleteWaitingStatus}>
							<AudioLines
								aria-hidden="true"
								className={CLASS_NAMES.setupCompleteWaitingIcon}
							/>
							<Text
								className={CLASS_NAMES.setupCompleteWaitingText}
								component="span"
								data-testid="stepper-setup-complete-waiting-status"
							>
								{localization.translate(
									'creator',
									'stepperOnboarding.setupComplete.waiting'
								)}
							</Text>
						</Box>
					) : null}
				</Box>
			</Box>
		</Modal>
	);
};

export default StepperSetupCompleteTestCallCard;
